import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { settlementAPI } from '../../services/api';

const openReceipt = async (rawUrl) => {
  if (!rawUrl) return;
  const url = rawUrl.replace(/\/uploads\/(receipt-[^/?#]+)/, '/api/receipts/$1');
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('server error');
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
  } catch {
    toast.error('تعذّر فتح الإيصال');
  }
};
const fmt = (n) =>
  Number(n || 0).toLocaleString('ar-SD', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_MAP = {
  pending:   { label: 'غير مسدد',       cls: 'bg-amber-100 text-amber-700' },
  submitted: { label: 'في انتظار التأكيد', cls: 'bg-blue-100 text-blue-700' },
  paid:      { label: 'مسدد ومؤكد ✓',   cls: 'bg-green-100 text-green-700' },
  rejected:  { label: 'مرفوض — أعد الإرسال', cls: 'bg-red-100 text-red-700' },
  none:      { label: 'لا توجد حجوزات', cls: 'bg-gray-100 text-gray-400' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.none;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
};

// ── Booking detail sub-table ──────────────────────────────────────────────────

const BookingTable = ({ bookings }) => {
  if (!bookings?.length) return <p className="text-sm text-gray-400 py-4 text-center">لا توجد حجوزات</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500">
          <tr>
            <th className="px-4 py-2.5 text-right font-medium">رقم التذكرة</th>
            <th className="px-4 py-2.5 text-right font-medium">المسافرون</th>
            <th className="px-3 py-2.5 text-center font-medium">الخط</th>
            <th className="px-3 py-2.5 text-center font-medium">التاريخ</th>
            <th className="px-3 py-2.5 text-center font-medium">مقاعد</th>
            <th className="px-3 py-2.5 text-center font-medium">المبلغ</th>
            <th className="px-3 py-2.5 text-center font-medium">العمولة</th>
            <th className="px-3 py-2.5 text-center font-medium">المصدر</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {bookings.map((b, i) => (
            <tr key={i} className="hover:bg-gray-50/60">
              <td className="px-4 py-2.5">
                {b.ticketNumbers?.length > 0 ? (
                  <div className="space-y-0.5">
                    {b.ticketNumbers.map((tn, ti) => (
                      <div key={ti} className="font-mono text-xs text-blue-700 font-semibold whitespace-nowrap">{tn}</div>
                    ))}
                  </div>
                ) : (
                  <span className="font-mono text-xs text-gray-400 whitespace-nowrap">{b.bookingNumber}</span>
                )}
              </td>
              <td className="px-4 py-2.5 max-w-[200px]">
                {b.passengers?.length > 0 ? (
                  <div className="space-y-0.5">
                    {b.passengers.map((p, pi) => (
                      <div key={pi} className="flex items-center gap-1.5 text-xs text-gray-700">
                        <span className="inline-flex w-5 h-5 bg-blue-100 text-blue-600 rounded-full items-center justify-center font-bold flex-shrink-0 text-[10px]">
                          {p.seat}
                        </span>
                        <span>{p.name || '—'}</span>
                      </div>
                    ))}
                  </div>
                ) : <span className="text-gray-400 text-xs">—</span>}
              </td>
              <td className="px-3 py-2.5 text-center text-xs text-gray-600 whitespace-nowrap">{b.route || '—'}</td>
              <td className="px-3 py-2.5 text-center text-xs text-gray-500 whitespace-nowrap">
                {new Date(b.createdAt).toLocaleDateString('ar-EG')}
              </td>
              <td className="px-3 py-2.5 text-center text-gray-600">{b.seats}</td>
              <td className="px-3 py-2.5 text-center font-medium text-gray-800">{fmt(b.totalAmount)}</td>
              <td className="px-3 py-2.5 text-center font-semibold text-blue-700">{fmt(b.commissionAmount)}</td>
              <td className="px-3 py-2.5 text-center">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  b.bookingSource === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                }`}>
                  {b.bookingSource === 'online' ? 'إلكتروني' : 'مكتب'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Payment submission modal ──────────────────────────────────────────────────

const PaymentModal = ({ row, onClose, onSuccess }) => {
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview('pdf');
    }
  };

  const handleSubmit = async () => {
    if (!file) { toast.error('يرجى اختيار سند التحويل'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('receipt',     file);
      fd.append('periodType',  row.period.periodType || 'monthly');
      fd.append('periodStart', row.period.start);
      fd.append('periodEnd',   row.period.end);
      await settlementAPI.submitPayment(fd);
      toast.success('تم إرسال سند الدفع بنجاح — في انتظار تأكيد المدير العام');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل الإرسال');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-800 text-base">إرفاق سند الدفع</h3>
            <p className="text-xs text-gray-400 mt-0.5">{row.period.label} &nbsp;·&nbsp; عمولة: <strong className="text-blue-700">{fmt(row.commissionAmount)} SDG</strong></p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-400">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Upload area */}
          <div>
            <label className="block text-sm text-gray-600 mb-2 font-medium">سند التحويل البنكي</label>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition"
            >
              {preview ? (
                preview === 'pdf' ? (
                  <div className="flex flex-col items-center gap-2 text-blue-700">
                    <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-sm font-medium">{file?.name}</span>
                    <span className="text-xs text-gray-400">انقر لتغيير الملف</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <img src={preview} alt="preview" className="max-h-40 mx-auto rounded-xl object-contain" />
                    <p className="text-xs text-gray-400">انقر لتغيير الصورة</p>
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">اضغط لرفع صورة أو ملف PDF</p>
                  <p className="text-xs">JPG, PNG, PDF — حد أقصى 5 MB</p>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} />
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">عدد الحجوزات</span>
              <span className="font-medium text-gray-800">{row.bookingCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">إجمالي الإيرادات</span>
              <span className="font-medium text-gray-800">{fmt(row.totalRevenue)} SDG</span>
            </div>
            <div className="flex justify-between border-t pt-1.5 mt-1">
              <span className="font-semibold text-gray-700">العمولة المستحقة</span>
              <span className="font-bold text-blue-700">{fmt(row.commissionAmount)} SDG</span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting || !file}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 rounded-xl font-semibold text-sm transition"
          >
            {submitting ? 'جاري الإرسال...' : 'إرسال سند الدفع'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Export helpers (reused from super admin page) ─────────────────────────────

const handleExportPdf = async (row) => {
  try {
    const res = await settlementAPI.exportPdf({
      periodStart: row.period.start,
      periodEnd:   row.period.end,
      periodType:  row.period.periodType || 'monthly',
    });
    const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `obligations_${row.period.start}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error('فشل تحميل الملف');
  }
};

// ── Period selector ───────────────────────────────────────────────────────────

const PERIOD_LABELS = { daily: 'يومي', weekly: 'أسبوعي', monthly: 'شهري' };

const PeriodSelector = ({ value, onChange }) => (
  <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
    {Object.entries(PERIOD_LABELS).map(([key, label]) => (
      <button key={key} onClick={() => onChange(key)}
        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
          value === key ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'
        }`}>
        {label}
      </button>
    ))}
  </div>
);

// ── Date range search bar ─────────────────────────────────────────────────────

const DateRangeBar = ({ dateFrom, dateTo, onFromChange, onToChange, onSearch, onClear, hasCustom }) => (
  <div className="flex items-center gap-2 flex-wrap">
    <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <label className="text-xs text-gray-400 ml-1">من</label>
      <input type="date" value={dateFrom} onChange={(e) => onFromChange(e.target.value)}
        className="text-sm text-gray-700 bg-transparent focus:outline-none w-32" />
    </div>
    <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      <label className="text-xs text-gray-400 ml-1">إلى</label>
      <input type="date" value={dateTo} onChange={(e) => onToChange(e.target.value)}
        className="text-sm text-gray-700 bg-transparent focus:outline-none w-32" />
    </div>
    <button onClick={onSearch} disabled={!dateFrom || !dateTo}
      className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
      </svg>
      بحث
    </button>
    {hasCustom && (
      <button onClick={onClear}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 bg-gray-100 hover:bg-red-50 px-3 py-2 rounded-xl transition">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        مسح
      </button>
    )}
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────

const CompanyObligationsPage = () => {
  const [periodType, setPeriodType] = useState('monthly');
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [expanded, setExpanded]     = useState(null);
  const [payModal, setPayModal]     = useState(null);
  const [exportingIdx, setExportingIdx] = useState(null);

  // Date range search
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo,   setDateTo]       = useState('');
  const [activeRange, setActiveRange] = useState(null);

  const load = useCallback((customRange) => {
    setLoading(true);
    const params = customRange
      ? { dateFrom: customRange.dateFrom, dateTo: customRange.dateTo }
      : { periodType, periods: 6 };
    settlementAPI.getMyObligations(params)
      .then((res) => setRows(res.data.data?.rows || []))
      .catch(() => toast.error('فشل تحميل المستحقات'))
      .finally(() => setLoading(false));
  }, [periodType]);

  useEffect(() => { load(null); }, [load]);

  const handleSearch = () => {
    if (!dateFrom || !dateTo) return;
    if (dateFrom > dateTo) { toast.error('تاريخ البداية يجب أن يكون قبل تاريخ النهاية'); return; }
    setExpanded(null);
    setActiveRange({ dateFrom, dateTo });
    load({ dateFrom, dateTo });
  };

  const handleClear = () => {
    setActiveRange(null);
    setDateFrom('');
    setDateTo('');
    load(null);
  };

  const totalPending = rows
    .filter((r) => ['pending', 'rejected'].includes(r.status))
    .reduce((s, r) => s + r.commissionAmount, 0);
  const totalSubmitted = rows
    .filter((r) => r.status === 'submitted')
    .reduce((s, r) => s + r.commissionAmount, 0);
  const totalPaid = rows
    .filter((r) => r.status === 'paid')
    .reduce((s, r) => s + r.commissionAmount, 0);

  const handleExport = async (row, idx) => {
    setExportingIdx(idx);
    await handleExportPdf({ ...row, period: { ...row.period, periodType: activeRange ? 'custom' : periodType } });
    setExportingIdx(null);
  };

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6 max-w-5xl mx-auto">

      {payModal && (
        <PaymentModal
          row={{ ...payModal, period: { ...payModal.period, periodType } }}
          onClose={() => setPayModal(null)}
          onSuccess={() => load(activeRange)}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">مستحقات المنصة</h1>
        <p className="text-sm text-gray-500 mt-1">
          العمولات المستحقة على شركتك لصالح منصة سافر — يمكنك تقديم سند الدفع لكل فترة.
        </p>
      </div>

      {/* Summary strip */}
      {!loading && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl shadow p-5 border-r-4 border-amber-400">
            <p className="text-xs text-gray-500 mb-1">عمولات غير مسددة</p>
            <p className="text-xl font-bold text-gray-800">{fmt(totalPending)} <span className="text-sm font-normal text-gray-400">SDG</span></p>
          </div>
          <div className="bg-white rounded-2xl shadow p-5 border-r-4 border-blue-400">
            <p className="text-xs text-gray-500 mb-1">قيد المراجعة</p>
            <p className="text-xl font-bold text-gray-800">{fmt(totalSubmitted)} <span className="text-sm font-normal text-gray-400">SDG</span></p>
          </div>
          <div className="bg-white rounded-2xl shadow p-5 border-r-4 border-green-400">
            <p className="text-xs text-gray-500 mb-1">مسدد ومؤكد</p>
            <p className="text-xl font-bold text-gray-800">{fmt(totalPaid)} <span className="text-sm font-normal text-gray-400">SDG</span></p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PeriodSelector value={periodType} onChange={(v) => { setPeriodType(v); if (!activeRange) load(null); }} />
          <p className="text-xs text-gray-400">اضغط على الفترة لعرض تفاصيل الحجوزات</p>
        </div>
        <DateRangeBar
          dateFrom={dateFrom}
          dateTo={dateTo}
          onFromChange={setDateFrom}
          onToChange={setDateTo}
          onSearch={handleSearch}
          onClear={handleClear}
          hasCustom={!!activeRange}
        />
        {activeRange && (
          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            نتائج البحث من <strong className="mx-1">{activeRange.dateFrom}</strong> إلى <strong className="mx-1">{activeRange.dateTo}</strong>
          </div>
        )}
      </div>

      {/* Period list */}
      {loading ? (
        <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-gray-400">لا توجد بيانات</div>
      ) : (
        <div className="space-y-4">
          {rows.map((row, ri) => {
            const isOpen = expanded === ri;
            const canPay = ['pending', 'rejected'].includes(row.status) && row.commissionAmount > 0;

            return (
              <div key={ri} className="bg-white rounded-2xl shadow overflow-hidden">
                {/* Row header */}
                <div className="flex items-center justify-between px-5 py-4 gap-3 flex-wrap">
                  {/* Left: toggle + period info */}
                  <button
                    className="flex items-center gap-3 text-right flex-1 min-w-0"
                    onClick={() => setExpanded(isOpen ? null : ri)}
                  >
                    <svg
                      className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-gray-800">{row.period.label}</span>
                        <span className="text-xs text-gray-400">{row.period.start} — {row.period.end}</span>
                        <StatusBadge status={row.status} />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {row.bookingCount} حجز · إيرادات {fmt(row.totalRevenue)} SDG
                      </p>
                    </div>
                  </button>

                  {/* Right: commission amount + actions */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-left">
                      <p className="text-xs text-gray-400">العمولة المستحقة</p>
                      <p className={`font-bold text-lg ${row.status === 'paid' ? 'text-green-600' : 'text-blue-700'}`}>
                        {fmt(row.commissionAmount)} <span className="text-xs font-normal text-gray-400">SDG</span>
                      </p>
                    </div>

                    {/* Submitted: show receipt button (authenticated fetch) */}
                    {row.status === 'submitted' && row.paymentProofUrl && (
                      <button
                        onClick={() => openReceipt(row.paymentProofUrl)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        عرض السند المرسل
                      </button>
                    )}

                    {/* Rejection note */}
                    {row.status === 'rejected' && row.rejectionNote && (
                      <div className="max-w-[180px] bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
                        <p className="text-xs text-red-600 font-medium">سبب الرفض:</p>
                        <p className="text-xs text-red-500">{row.rejectionNote}</p>
                      </div>
                    )}

                    {/* Pay button */}
                    {canPay && (
                      <button
                        onClick={() => setPayModal(row)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-semibold transition"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        {row.status === 'rejected' ? 'إعادة إرسال السند' : 'سداد المستحقات'}
                      </button>
                    )}

                    {/* Export PDF */}
                    {row.bookingCount > 0 && (
                      <button
                        onClick={() => handleExport(row, ri)}
                        disabled={exportingIdx === ri}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 bg-gray-50 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition"
                        title="تصدير تقرير PDF"
                      >
                        {exportingIdx === ri ? (
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                        PDF
                      </button>
                    )}
                  </div>
                </div>

                {/* Booking detail (expanded) */}
                {isOpen && (
                  <div className="border-t px-5 py-4">
                    <BookingTable bookings={row.bookings} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CompanyObligationsPage;
